import { useState } from 'react';

import { Modal } from '@/components/Modal';
import { PageHeader } from '@/components/PageHeader';
import { QueryState } from '@/components/QueryState';
import { useFieldGrants, useSaveWbsActual, useWbsActuals, type WbsActual } from '@/lib/field';
import { percent, shortDate } from '@/lib/format';
import { flattenProgress, isBehind, rollupProgress } from '@/lib/progress';
import { useSiteProgress } from '@/lib/queries';
import { useSite } from '@/lib/useSite';
import type { SiteProgress } from '@/lib/types';

/** 웹 공정관리 탭의 키 — 실적 보기·쓰기가 이 하나로 열린다(`SiteWbsController`). */
const PROGRESS_MENU = 'site_pmis_progress';

/**
 * 공정.
 *
 * <p>맨 위의 <b>전체 공정율</b>이 이 화면의 답이다. 「지금 몇 %인가」를 알려고 들어오는 화면이라
 * 목록보다 그 값이 먼저 커야 한다. 접는 식은 backend 와 같다(`lib/progress.ts`) — 같은 현장인데
 * 내부 화면과 포털의 숫자가 다르면 어느 쪽이 맞는지 아무도 모른다.
 *
 * <p>실적이 계획보다 낮으면 <b>붉게</b> 쓴다. 안전의 「서명 필요」와 같은 약속이다 — 붉은색은
 * 오늘 볼 것이라는 뜻이고, 그러려면 다른 데 쓰지 않아야 한다.
 *
 * <p><b>직원은 여기서 실적을 적는다</b>(사용자 지시 2026-10-27). 실적 착수·완료일과 누계 물량 — 웹 공정표
 * 격자의 그 세 칸이고, 문도 같다(`site_pmis_progress` 조회·수정 권한). 완료 처리된 줄은 잠겨 손을 세우지 않는다.
 */
export function ProgressPage() {
  const { siteId, siteName, isStaff } = useSite();
  const progress = useSiteProgress(siteId);
  const rows = progress.data ?? [];
  const { can } = useFieldGrants();
  const actuals = useWbsActuals(siteId, isStaff && can(PROGRESS_MENU, 'view'));
  const actualById = new Map((actuals.data ?? []).map((one) => [one.itemId, one]));
  const canEditActual = isStaff && can(PROGRESS_MENU, 'edit');
  const [editing, setEditing] = useState<{ row: SiteProgress; actual: WbsActual } | null>(null);

  const rollup = rollupProgress(rows);
  const nodes = flattenProgress(rows);
  const behind = rollup.actualRate !== null && rollup.plannedRate !== null && rollup.actualRate < rollup.plannedRate;

  return (
    <>
      <PageHeader title="공정" subtitle={siteName} />
      <QueryState
        pending={progress.isPending}
        error={progress.isError}
        empty={rows.length === 0}
        emptyText="등록된 공정이 없습니다."
      >
        <div className="card mb-3">
          <div className="text-ui-2xs" style={{ color: 'var(--text-muted)' }}>전체 공정율</div>
          <div className="mt-0.5 flex items-baseline gap-2">
            <span
              className="text-ui-3xl font-semibold"
              style={behind ? { color: 'var(--danger)' } : undefined}
            >
              {percent(rollup.actualRate)}
            </span>
            <span className="text-ui-sm" style={{ color: 'var(--text-secondary)' }}>
              계획 {percent(rollup.plannedRate)}
            </span>
          </div>
          <div className="mt-2">
            <RateBar planned={rollup.plannedRate} actual={rollup.actualRate} />
          </div>
          <div className="mt-2 text-ui-2xs" style={{ color: behind ? 'var(--danger)' : 'var(--text-muted)' }}>
            {rollup.countedItems === 0
              ? '실적이 기록된 공정이 없습니다.'
              : behind
                ? `계획보다 ${gap(rollup.plannedRate, rollup.actualRate)} 뒤져 있습니다 · 지연 공정 ${rollup.behindCount}개`
                : `공정 ${rollup.countedItems}개`}
          </div>
        </div>

        <ul className="flex flex-col gap-2">
          {nodes.map((node) => {
            const actual = actualById.get(node.row.id);
            return (
              <ProgressCard
                key={node.row.id}
                row={node.row}
                depth={node.depth}
                leaf={node.leaf}
                actual={actual}
                onEdit={canEditActual && actual !== undefined && !actual.completed
                  ? () => setEditing({ row: node.row, actual })
                  : undefined}
              />
            );
          })}
        </ul>
      </QueryState>

      {editing !== null && (
        <ActualModal siteId={siteId} row={editing.row} actual={editing.actual} onClose={() => setEditing(null)} />
      )}
    </>
  );
}

/** 실적 한 줄 — 「착수 ~ 완료 · 누계 물량」. 하나도 없으면 줄을 세우지 않는다. */
function actualLabel(actual: WbsActual): string | null {
  const parts: string[] = [];
  if (actual.actualStartDate !== null || actual.actualEndDate !== null) {
    parts.push(`${actual.actualStartDate !== null ? shortDate(actual.actualStartDate) : ''} ~ ${actual.actualEndDate !== null ? shortDate(actual.actualEndDate) : ''}`);
  }
  if (actual.actualQuantity !== null) {
    parts.push(`${actual.actualQuantity.toLocaleString('ko-KR')}${actual.plannedQuantity !== null ? ` / ${actual.plannedQuantity.toLocaleString('ko-KR')}` : ''}${actual.unit ? ` ${actual.unit}` : ''}`);
  }
  if (actual.completed) parts.push('완료');
  return parts.length === 0 ? null : `실적 ${parts.join(' · ')}`;
}

/**
 * 실적 적기. 물량은 <b>누계</b>다 — 웹 격자와 같은 칸이라 그날 한 양을 적으면 공정율이 뒤로 간다.
 * 버튼은 제목 줄에 둔다(모달 규칙).
 */
function ActualModal({
  siteId,
  row,
  actual,
  onClose,
}: {
  siteId: string;
  row: SiteProgress;
  actual: WbsActual;
  onClose: () => void;
}) {
  const save = useSaveWbsActual(siteId);
  const [start, setStart] = useState(actual.actualStartDate ?? '');
  const [end, setEnd] = useState(actual.actualEndDate ?? '');
  const [quantity, setQuantity] = useState(actual.actualQuantity !== null ? String(actual.actualQuantity) : '');

  const submit = () => {
    const amount = quantity.trim() === '' ? null : Number(quantity.replace(/[^0-9.]/g, ''));
    save.mutate(
      {
        itemId: actual.itemId,
        body: {
          actualStartDate: start === '' ? null : start,
          actualEndDate: end === '' ? null : end,
          actualQuantity: amount !== null && Number.isFinite(amount) ? amount : null,
        },
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Modal
      title={row.name}
      onClose={onClose}
      actions={
        <>
          <button type="button" className="btn-primary px-3 py-1.5 text-ui-xs" disabled={save.isPending} onClick={submit}>
            {save.isPending ? '저장 중…' : '저장'}
          </button>
          <button type="button" className="btn-secondary px-3 py-1.5 text-ui-xs" onClick={onClose}>
            닫기
          </button>
        </>
      }
    >
      <div className="mb-3 flex gap-2">
        <label className="flex-1">
          <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>실적 착수</span>
          <input type="date" className="input-field" value={start} onChange={(e) => setStart(e.target.value)} />
        </label>
        <label className="flex-1">
          <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>실적 완료</span>
          <input type="date" className="input-field" value={end} onChange={(e) => setEnd(e.target.value)} />
        </label>
      </div>
      <label className="block">
        <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>
          누계 실적물량{actual.unit ? ` (${actual.unit})` : ''}
          {actual.plannedQuantity !== null && ` · 계획 ${actual.plannedQuantity.toLocaleString('ko-KR')}`}
        </span>
        <input
          type="text"
          inputMode="decimal"
          className="input-field"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
        />
      </label>
      {save.isError && (
        <p className="mt-3 text-ui-xs" style={{ color: 'var(--danger)' }}>저장하지 못했습니다.</p>
      )}
    </Modal>
  );
}

/** 들여쓰기의 한계. 깊은 나무에서 안쪽 줄이 화면 밖으로 밀리는 것보다 같은 자리에 서는 편이 낫다. */
const MAX_INDENT_DEPTH = 3;

/** 공정 한 줄. 묶음 공정(자식이 있는 줄)은 굵게 세워 나무의 마디가 보이게 한다. */
function ProgressCard({
  row,
  depth,
  leaf,
  actual,
  onEdit,
}: {
  row: SiteProgress;
  depth: number;
  leaf: boolean;
  /** 직원에게만 온다 — 공정표 격자의 실적 칸. 리프만 있다. */
  actual?: WbsActual;
  onEdit?: () => void;
}) {
  const period = planPeriod(row);
  const actualText = actual !== undefined ? actualLabel(actual) : null;

  return (
    <li className="card" style={{ marginLeft: Math.min(depth, MAX_INDENT_DEPTH) * 10 }}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className={`truncate text-ui-md${leaf ? '' : ' font-medium'}`}>{row.name}</div>
          <div className="truncate text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
            {row.code}
            {period !== null && ` · ${period}`}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="text-ui-lg font-semibold" style={isBehind(row) ? { color: 'var(--danger)' } : undefined}>
            {percent(row.actualRate)}
          </div>
          <div className="text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
            계획 {percent(row.plannedRate)}
          </div>
        </div>
      </div>
      <div className="mt-2">
        <RateBar planned={row.plannedRate} actual={row.actualRate} />
      </div>
      {(actualText !== null || onEdit !== undefined) && (
        <div className="mt-2 flex items-center justify-between gap-2">
          <span className="min-w-0 truncate text-ui-2xs" style={{ color: 'var(--text-secondary)' }}>
            {actualText}
          </span>
          {onEdit !== undefined && (
            <button type="button" className="btn-secondary shrink-0 px-3 py-1.5 text-ui-xs" onClick={onEdit}>
              실적
            </button>
          )}
        </div>
      )}
    </li>
  );
}

/** 계획 기간. 한쪽만 있으면 그 줄을 아예 적지 않는다 — 반쪽 기간은 읽는 사람을 멈추게 한다. */
function planPeriod(row: SiteProgress): string | null {
  if (row.plannedStartDate === null || row.plannedEndDate === null) return null;
  return `${shortDate(row.plannedStartDate)} ~ ${shortDate(row.plannedEndDate)}`;
}

/** 계획 대비 차이. 「몇 %p 뒤졌나」는 백분율의 차이라 단위가 %가 아니다. */
function gap(planned: number | null, actual: number | null): string {
  if (planned === null || actual === null) return '-';
  return `${(planned - actual).toFixed(1)}%p`;
}

/**
 * 계획·실적 막대.
 *
 * <p>막대 둘을 나란히 놓지 않고 <b>계획을 눈금으로</b> 세운다 — 물어보는 것이 「얼마나 했나」가
 * 아니라 「계획만큼 했나」라, 두 값의 자리가 겹쳐 보여야 한 눈에 답이 된다.
 */
function RateBar({ planned, actual }: { planned: number | null; actual: number | null }) {
  const done = clamp(actual);
  const behind = planned !== null && actual !== null && actual < planned;

  return (
    <div
      className="relative overflow-hidden rounded-full"
      style={{ height: 6, background: 'var(--bg-hover)' }}
      aria-hidden
    >
      <div
        className="absolute inset-y-0 left-0 rounded-full"
        style={{ width: `${done}%`, background: behind ? 'var(--danger)' : 'var(--accent)' }}
      />
      {planned !== null && (
        <div
          className="absolute inset-y-0"
          style={{ left: `${clamp(planned)}%`, width: 2, background: 'var(--text-secondary)' }}
        />
      )}
    </div>
  );
}

function clamp(value: number | null): number {
  if (value === null) return 0;
  return Math.max(0, Math.min(100, value));
}
